import React from 'react'
import ReactDOM from 'react-dom'
import Bugsnag from '@bugsnag/js'
import BugsnagPluginReact from '@bugsnag/plugin-react'
import BugsnagPerformance from '@bugsnag/browser-performance'
import { Provider } from '@leme/shared-components'
import '@leme/default-css'
import 'sanitize.css'
import Routes from './routes'
import MenuProvider from './contexts/MenuContext'
import { LibraryContextProvider } from './contexts/LibraryContext'
import { QueryClientProvider } from '@tanstack/react-query'
import queryClient from './config/queryClient'

import '@leme/default-css'
import '@leme/shared-components/style.css'
import 'sanitize.css'

const { REACT_APP_BUGSNAG_API_KEY } = process.env

const SUPPRESSED_ERROR_PATTERNS = [
  // JWT auth errors — expected flow, not actionable
  '"isAuthError":true',
  'Invalid or missing JWT token',
  // Network errors — not actionable
  'Failed to fetch',
  'Load failed',
  'NetworkError when attempting to fetch resource',
  // Marc21 404 — expected when record not found
  '"detail":"Not Found"',
  // epubjs section.load() completes after book.destroy() on fast back navigation
  "Cannot read properties of undefined (reading 'content')",
]

const shouldSuppressError = (message) =>
  SUPPRESSED_ERROR_PATTERNS.some((pattern) => message.includes(pattern))

const MAX_DIAGNOSTIC_ENTRIES = 30
const recentDomLookups = []
const recentUserEvents = []

const pushDiagnosticEntry = (buffer, entry) => {
  buffer.push({
    ...entry,
    timestamp: new Date().toISOString()
  })

  if (buffer.length > MAX_DIAGNOSTIC_ENTRIES) {
    buffer.shift()
  }
}

const getShortStack = () => {
  try {
    throw new Error('DOM lookup trace')
  } catch (error) {
    return (error?.stack || '')
      .split('\n')
      .slice(2, 8)
      .map((line) => line.trim())
      .join('\n')
  }
}

const getSafeSelectorState = (selector) => {
  try {
    return Boolean(document.querySelector(selector))
  } catch {
    return false
  }
}

const getDomSnapshot = () => ({
  pathname: window.location.pathname,
  search: window.location.search,
  hash: window.location.hash,
  href: window.location.href,
  readyState: document.readyState,
  visibilityState: document.visibilityState,
  activeElement: document.activeElement
    ? {
      tagName: document.activeElement.tagName,
      id: document.activeElement.id || null,
      className: document.activeElement.className || null
    }
    : null,
  selectors: {
    '#root': getSafeSelectorState('#root'),
    '#scroll': getSafeSelectorState('#scroll'),
    '#leme-navbar': getSafeSelectorState('#leme-navbar'),
    '#leme-mobile-navbar': getSafeSelectorState('#leme-mobile-navbar'),
    '#search-overlay': getSafeSelectorState('#search-overlay')
  }
})

const setupDomLookupTracking = () => {
  if (typeof window === 'undefined' || window.__lemeDomLookupTrackingEnabled__) return

  window.__lemeDomLookupTrackingEnabled__ = true

  const originalDocumentQuerySelector = document.querySelector.bind(document)
  document.querySelector = (selector) => {
    const element = originalDocumentQuerySelector(selector)

    if (!element) {
      pushDiagnosticEntry(recentDomLookups, {
        method: 'document.querySelector',
        selector: String(selector),
        pathname: window.location.pathname,
        stack: getShortStack()
      })
    }

    return element
  }

  const originalDocumentGetElementById = document.getElementById.bind(document)
  document.getElementById = (id) => {
    const element = originalDocumentGetElementById(id)

    if (!element) {
      pushDiagnosticEntry(recentDomLookups, {
        method: 'document.getElementById',
        selector: String(id),
        pathname: window.location.pathname,
        stack: getShortStack()
      })
    }

    return element
  }
}

const setupUserInteractionTracking = () => {
  if (typeof window === 'undefined' || window.__lemeUserInteractionTrackingEnabled__) return

  window.__lemeUserInteractionTrackingEnabled__ = true

  const track = (eventName) => (event) => {
    const target = event?.target
    pushDiagnosticEntry(recentUserEvents, {
      eventName,
      pathname: window.location.pathname,
      targetTagName: target?.tagName || null,
      targetId: target?.id || null,
      targetClassName: target?.className || null
    })
  }

  window.addEventListener('click', track('click'), true)
  window.addEventListener('keydown', track('keydown'), true)
  window.addEventListener('submit', track('submit'), true)
  window.addEventListener('popstate', track('popstate'), true)
  window.addEventListener('hashchange', track('hashchange'), true)
}

setupDomLookupTracking()
setupUserInteractionTracking()

Bugsnag.start({
  apiKey: REACT_APP_BUGSNAG_API_KEY,
  plugins: [new BugsnagPluginReact()],
  appVersion: "v2--2026-08-manual-deploy",
  onError: (event) => {
    const firstErrorMessage = event?.errors?.[0]?.errorMessage || ''
    const originalMessage = event?.originalError?.message || ''
    const message = firstErrorMessage || originalMessage

    if (shouldSuppressError(message)) return false

    event.addMetadata('runtime_context', getDomSnapshot())
    event.addMetadata('frontend_diagnostic', {
      message,
      recentDomLookups,
      recentUserEvents
    })
  }
})

BugsnagPerformance.start({ apiKey: REACT_APP_BUGSNAG_API_KEY })

const ErrorBoundary = Bugsnag.getPlugin('react').createErrorBoundary(React)

ReactDOM.render(
  <QueryClientProvider client={queryClient}>
    <ErrorBoundary>
      <React.StrictMode>
        <LibraryContextProvider>
          <MenuProvider>
            <Provider>
              <Routes />
            </Provider>
          </MenuProvider>
        </LibraryContextProvider>
      </React.StrictMode>
    </ErrorBoundary>
  </QueryClientProvider>,
  document.getElementById('root')
)
